
import { Component, Prop, Vue } from 'vue-property-decorator'
import { namespace, Action, Getter, State } from 'vuex-class'
import type { Cid, Id, Post, PopoverAnchor, User } from 'types'
import { navigateTo } from 'bits/location'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzMenuRows, {
  OzMenuRowsFocusStyle,
  OzMenuRowsRow,
  OzMenuRowsSizePreset,
  OzMenuRowsRowStyle,
} from 'library/v4/components/OzMenuRows.vue'
import { OzBoxColors } from 'library/v4/components/OzBox.vue'
import OzScreenSizeAwarePopoverModal from 'library/v4/components/OzScreenSizeAwarePopoverModal.vue'
import { isNew, isEmpty } from 'vuexstore/helpers/post'

enum PostActionType {
  OpenPostInNewTab = 'OpenPostInNewTab',
  OpenAttachment = 'OpenAttachment',
  DownloadAttachment = 'DownloadAttachment',
  CopyAttachmentLink = 'CopyAttachmentLink',
  CopyPostLink = 'CopyPostLink',
  EditPost = 'EditPost',
  TransferPost = 'TransferPost',
  CopyPost = 'CopyPost',
  UnsetAsCover = 'UnsetAsCover',
  SetAsCover = 'SetAsCover',
  DeletePost = 'DeletePost',
}

const browser = namespace('browser')
const post = namespace('post')
const postAction = namespace('postAction')

@Component({
  components: { OzIcon, OzMenuRows, OzScreenSizeAwarePopoverModal },
})
export default class SurfacePanelPostExpandedActionMenu extends Vue {
  OzBoxColors = OzBoxColors
  OzMenuRowsFocusStyle = OzMenuRowsFocusStyle
  OzMenuRowsSizePreset = OzMenuRowsSizePreset

  @Prop({
    type: String,
    default: null,
    validator: (pid: string) => pid.startsWith('c'),
  })
  postCid!: string

  @Prop({ required: true }) popoverAnchor!: PopoverAnchor

  @Prop({ default: false }) attachmentIsDownloadable!: boolean

  @Prop() attachmentUrl?: string

  @Prop() downloadUrl?: string

  @Getter dir!: string
  @Getter isUsingNewSurfaceShadows!: boolean
  @State isFirefox!: boolean
  @State user!: User
  @browser.Getter isPhone!: boolean
  @browser.Getter isSmallerThanDesktop!: boolean
  @browser.Getter isSmallerThanTabletPortrait!: boolean
  @browser.Getter windowHeight!: number
  @browser.State darkMode!: boolean
  @post.Getter postEntitiesByCid!: Record<Cid, Post>
  @postAction.Getter('canEditPostUnderAction') canEdit!: boolean
  @postAction.Getter('canTransferPostUnderAction') canTransfer!: boolean
  @postAction.Getter('canCopyPostUnderAction') canCopy!: boolean
  @postAction.Getter('canChangeCoverToExpandedPost') canChangeCoverPost!: boolean
  @postAction.Getter('isExpandedPostPreviewPost') isPreviewPost!: boolean
  @postAction.Getter('canDeletePostUnderAction') canDelete!: boolean

  get post(): Post {
    return this.postEntitiesByCid[this.postCid]
  }

  /**
   * ACTIONS
   */

  get actionListGroups(): OzMenuRowsRow[][] {
    const group1: OzMenuRowsRow[] = []
    const group2: OzMenuRowsRow[] = []
    const group3: OzMenuRowsRow[] = []
    const group4: OzMenuRowsRow[] = []
    const group5: OzMenuRowsRow[] = []

    // Group 1
    group1.push({
      icon: 'post_open',
      text: this.__('Open post in new tab'),
      eventPayload: PostActionType.OpenPostInNewTab,
    })
    group1.push({
      icon: 'post_copy_link',
      text: this.__('Copy link to post'),
      eventPayload: PostActionType.CopyPostLink,
    })

    // Group 2
    if (this.attachmentIsDownloadable && this.downloadUrl) {
      group2.push({
        icon: 'attachment_download',
        text: this.__('Download attachment'),
        eventPayload: PostActionType.DownloadAttachment,
      })
      group2.push({
        icon: 'attachment_copy_link',
        text: this.__('Copy direct link to attachment'),
        eventPayload: PostActionType.CopyAttachmentLink,
      })
    } else if (this.attachmentUrl) {
      group2.push({
        icon: 'attachment_open',
        text: this.__('Open attachment in new tab'),
        eventPayload: PostActionType.OpenAttachment,
      })
      group2.push({
        icon: 'attachment_copy_link',
        text: this.__('Copy attachment link'),
        eventPayload: PostActionType.CopyAttachmentLink,
      })
    }

    // Group 3
    if (this.canEdit) {
      group3.push({
        icon: 'pencil_outline',
        text: this.__('Edit post'),
        eventPayload: PostActionType.EditPost,
      })
    }

    // Group 4
    if (this.canCopy) {
      group4.push({
        icon: 'post_duplicate',
        text: this.__('Duplicate post'),
        eventPayload: PostActionType.CopyPost,
      })
    }
    if (this.canTransfer) {
      group4.push({
        icon: 'post_transfer',
        text: this.__('Transfer post'),
        eventPayload: PostActionType.TransferPost,
      })
    }
    if (this.canChangeCoverPost && this.isPreviewPost) {
      group4.push({
        icon: 'padlet_cover',
        text: this.__('Unset as padlet cover'),
        eventPayload: PostActionType.UnsetAsCover,
      })
    }
    if (this.canChangeCoverPost && !this.isPreviewPost) {
      group4.push({
        icon: 'padlet_cover',
        text: this.__('Set as padlet cover'),
        eventPayload: PostActionType.SetAsCover,
      })
    }

    // Group 5
    if (this.canDelete) {
      group5.push({
        icon: 'delete',
        text: this.__('Delete post'),
        rowStyle: OzMenuRowsRowStyle.Danger,
        eventPayload: PostActionType.DeletePost,
      })
    }

    return [group1, group2, group3, group4, group5].filter((group) => group.length > 0)
  }

  @Action unsetCoverPost!: (payload: { postId: Id }) => void
  @Action setCoverPost!: (payload: { postId: Id }) => void
  @post.Action startEditingPost!: (payload: { postCid: Cid }) => void
  @post.Action deletePost!: (payload: { postCid: Cid }) => void
  @postAction.Action unexpandPost!: () => void
  @postAction.Action copyPostLinkAndAlert!: (payload: { postCid: Cid }) => void
  @postAction.Action copyAttachmentLinkAndAlert!: (payload: { postCid: Cid }) => void
  @postAction.Action startPostTransfer!: (payload: { transferPostCid: Cid }) => void
  @postAction.Action startPostCopy!: (payload: { copyPostCid: Cid }) => void
  @postAction.Action confirmDeletePost!: (payload: { postCid: Cid }) => void

  close(): void {
    this.$emit('close')
  }

  onSwipedown({ isAtTopBeforeDragging }: { isAtTopBeforeDragging: boolean }): void {
    if (!isAtTopBeforeDragging) {
      this.$emit('close')
    }
  }

  openPostInNewTab(): void {
    this.close()
    this.post.permalink && navigateTo(this.post.permalink, { target: '_blank' })
  }

  openAttachment(): void {
    this.close()
    this.attachmentUrl && navigateTo(this.attachmentUrl, { target: '_blank' })
  }

  downloadAttachment(): void {
    this.close()
    this.downloadUrl && navigateTo(this.downloadUrl, { target: '_blank' })
  }

  copyLink(): void {
    this.copyPostLinkAndAlert({ postCid: this.postCid })
    this.close()
  }

  copyAttachmentLink(): void {
    this.copyAttachmentLinkAndAlert({ postCid: this.postCid })
    this.close()
  }

  edit(): void {
    this.close()
    this.unexpandPost()
    this.startEditingPost({ postCid: this.post.cid })
  }

  transfer(): void {
    this.close()
    this.unexpandPost()
    this.startPostTransfer({ transferPostCid: this.post.cid })
  }

  copy(): void {
    this.close()
    this.unexpandPost()
    this.startPostCopy({ copyPostCid: this.post.cid })
  }

  unsetAsCover(): void {
    if (!this.post.id) return
    this.unsetCoverPost({ postId: this.post.id })
    this.close()
  }

  setAsCover(): void {
    if (!this.post.id) return
    this.setCoverPost({ postId: this.post.id })
    this.close()
  }

  askToDelete(): void {
    this.close()
    if (isNew(this.post) || isEmpty(this.post)) {
      this.unexpandPost()
      this.deletePost({ postCid: this.post.cid })
    } else {
      this.confirmDeletePost({ postCid: this.post.cid })
    }
  }

  get actionHandlers(): Record<PostActionType, () => void> {
    return {
      [PostActionType.OpenPostInNewTab]: this.openPostInNewTab,
      [PostActionType.OpenAttachment]: this.openAttachment,
      [PostActionType.DownloadAttachment]: this.downloadAttachment,
      [PostActionType.CopyAttachmentLink]: this.copyAttachmentLink,
      [PostActionType.CopyPostLink]: this.copyLink,
      [PostActionType.EditPost]: this.edit,
      [PostActionType.TransferPost]: this.transfer,
      [PostActionType.CopyPost]: this.copy,
      [PostActionType.UnsetAsCover]: this.unsetAsCover,
      [PostActionType.SetAsCover]: this.setAsCover,
      [PostActionType.DeletePost]: this.askToDelete,
    }
  }

  handleSelectedAction(_: unknown, postActionType: PostActionType): void {
    return this.actionHandlers[postActionType]()
  }
}
